import { getPlatformAccessToken, getPlatformIdentity } from "./admin-access";
import { defenseAnalytics } from "./analytics";
import {
  type ClientCommandPayload,
  type ConnectionState,
  type LobbyState,
  type MatchSnapshot,
} from "./multiplayer-protocol";

export interface MultiplayerDiagnostic {
  readonly state: ConnectionState;
  readonly endpoint: string;
  readonly lobbyCode: string | null;
  readonly matchId: string | null;
  readonly reconnectAttempts: number;
  readonly latencyMs: number | null;
  readonly lastError: string | null;
}

type Listener = () => void;
interface WireMessage {
  readonly type?: unknown;
  readonly requestId?: unknown;
  readonly payload?: unknown;
}

const REALTIME_URL =
  (import.meta.env.VITE_DEFENSE_REALTIME_URL as string | undefined)?.trim() ??
  "wss://realtime.littlecastle.online";
export class DefenseMultiplayerClient {
  private socket: WebSocket | null = null;
  private state: ConnectionState = "idle";
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private intentionalClose = false;
  private listeners = new Set<Listener>();
  private lobby: LobbyState | null = null;
  private snapshot: MatchSnapshot | null = null;
  private resumeToken: string | null = null;
  private latencyMs: number | null = null;
  private lastError: string | null = null;
  private localPlayerId: string | null = null;

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
  get connectionState(): ConnectionState {
    return this.state;
  }
  get lobbyState(): LobbyState | null {
    return this.lobby;
  }
  get matchSnapshot(): MatchSnapshot | null {
    return this.snapshot;
  }
  get playerId(): string | null {
    return this.localPlayerId;
  }

  diagnostic(): MultiplayerDiagnostic {
    return {
      state: this.state,
      endpoint: REALTIME_URL,
      lobbyCode: this.lobby?.code ?? null,
      matchId: this.snapshot?.matchId ?? null,
      reconnectAttempts: this.reconnectAttempts,
      latencyMs: this.latencyMs,
      lastError: this.lastError,
    };
  }

  async connect(): Promise<void> {
    if (this.socket?.readyState === WebSocket.OPEN || this.state === "connecting") return;
    this.intentionalClose = false;
    this.setState("authenticating");
    const identity = await getPlatformIdentity();
    if (!identity.authenticated)
      throw this.fail("Sign in to LittleCastle before using multiplayer.");
    const accessToken = await getPlatformAccessToken();
    if (accessToken === null) throw this.fail("LittleCastle access token is unavailable.");
    this.setState(this.reconnectAttempts > 0 ? "reconnecting" : "connecting");
    await new Promise<void>((resolve, reject) => {
      const socket = new WebSocket(REALTIME_URL, ["lc-v1", `lc-auth.${accessToken}`]);
      this.socket = socket;
      const timeout = window.setTimeout(() => {
        socket.close();
        reject(this.fail("Realtime connection timed out."));
      }, 10_000);
      socket.onopen = () => {
        window.clearTimeout(timeout);
        this.reconnectAttempts = 0;
        this.lastError = null;
        this.setState("connected");
        defenseAnalytics.track("multiplayer_connected", {});
        if (this.resumeToken) this.sendWire("session.resume", { resumeToken: this.resumeToken });
        resolve();
      };
      socket.onerror = () => {
        window.clearTimeout(timeout);
        reject(this.fail("Realtime connection failed."));
      };
      socket.onmessage = (event: MessageEvent<string>) => this.handleMessage(event.data);
      socket.onclose = () => {
        window.clearTimeout(timeout);
        this.socket = null;
        if (this.intentionalClose) {
          this.setState("disconnected");
          return;
        }
        this.setState("disconnected");
        this.scheduleReconnect();
      };
    });
  }

  disconnect(): void {
    this.intentionalClose = true;
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.socket?.close(1000, "client disconnect");
    this.socket = null;
    this.setState("disconnected");
  }

  send(payload: ClientCommandPayload): string {
    switch (payload.type) {
      case "lobby:create":
        return this.sendWire("lobby.create", {
          gameKey: "defense",
          mode: payload.mode,
          playerLimit: 2,
          visibility: payload.visibility,
          settings: {
            scenarioId: "castle-approach-1",
            difficulty: "normal",
            mapWidth: 24,
            mapHeight: 16,
            startingGold: 520,
            gateHealth: 30,
          },
        });
      case "lobby:join":
        return this.sendWire("lobby.join", { code: payload.code });
      case "lobby:leave":
        return this.sendWire("lobby.leave", {});
      case "lobby:ready":
        return this.sendWire("lobby.set-ready", { ready: payload.ready });
      case "match:start":
        return this.sendWire("lobby.start", {});
      case "match:resume":
        return this.sendWire("session.resume", { resumeToken: payload.resumeToken });
      case "match:command":
        return this.sendWire("match.action", payload.command);
      case "match:surrender":
        return this.sendWire("match.action", { action: "defense-surrender" });
      case "ping":
        return this.sendWire("connection.ping", { timestamp: payload.sentAt });
      case "lobby:configure":
        throw this.fail("Lobby configuration is not available in Phase 38.");
    }
  }

  private sendWire(type: string, payload: Readonly<Record<string, unknown>>): string {
    if (this.socket?.readyState !== WebSocket.OPEN)
      throw this.fail("Realtime connection is not open.");
    const requestId = crypto.randomUUID();
    this.socket.send(JSON.stringify({ type, requestId, payload }));
    return requestId;
  }

  private handleMessage(raw: string): void {
    let parsed: WireMessage;
    try {
      parsed = JSON.parse(raw) as WireMessage;
    } catch {
      this.fail("Realtime server sent invalid JSON.");
      return;
    }
    const type = typeof parsed.type === "string" ? parsed.type : "";
    const payload = this.record(parsed.payload);
    if (type === "connection.ready") {
      const user = this.record(payload.user);
      this.localPlayerId = this.stringValue(user.id, "") || null;
      this.resumeToken =
        typeof payload.resumeToken === "string" ? payload.resumeToken : this.resumeToken;
    } else if (type === "session.token") {
      this.resumeToken =
        typeof payload.resumeToken === "string" ? payload.resumeToken : this.resumeToken;
    } else if (
      [
        "lobby.created",
        "lobby.joined",
        "lobby.updated",
        "lobby.member-ready",
        "lobby.started",
      ].includes(type)
    ) {
      const lobby = this.record(payload.lobby);
      if (Object.keys(lobby).length > 0) this.lobby = this.mapLobby(lobby);
    } else if (type === "lobby.left" || type === "lobby.closed") {
      this.lobby = null;
    } else if (
      type === "match.started" ||
      type === "match.updated" ||
      type === "match.reconnected"
    ) {
      const match = this.record(payload.match);
      if (Object.keys(match).length > 0) this.snapshot = this.mapMatch(match);
    } else if (type === "connection.pong") {
      const timestamp = typeof payload.timestamp === "number" ? payload.timestamp : Date.now();
      this.latencyMs = Math.max(0, Date.now() - timestamp);
    } else if (type === "error" || type === "request.failed") {
      this.lastError = this.stringValue(payload.message, "Realtime request failed.");
    }
    this.emit();
  }

  private mapLobby(lobby: Record<string, unknown>): LobbyState {
    const members = Array.isArray(lobby.members) ? lobby.members : [];
    return {
      lobbyId: this.stringValue(lobby.id, ""),
      code: this.stringValue(lobby.code, ""),
      mode: lobby.mode === "head-to-head" ? "head-to-head" : "coop",
      visibility: lobby.visibility === "public" ? "public" : "private",
      hostPlayerId: this.stringValue(lobby.hostUserId, ""),
      players: members.map((member, slot) => {
        const entry = this.record(member);
        return {
          playerId: this.stringValue(entry.userId, ""),
          displayName: this.stringValue(entry.displayName, "Player"),
          ready: entry.ready === true,
          connected: true,
          slot,
        };
      }),
      scenarioId:
        typeof this.record(lobby.settings).scenarioId === "string"
          ? String(this.record(lobby.settings).scenarioId)
          : null,
      difficulty:
        typeof this.record(lobby.settings).difficulty === "string"
          ? String(this.record(lobby.settings).difficulty)
          : null,
      status:
        lobby.status === "started" ? "in-match" : lobby.status === "closed" ? "closed" : "waiting",
      revision: members.length,
    };
  }

  private mapMatch(match: Record<string, unknown>): MatchSnapshot {
    return {
      matchId: this.stringValue(match.matchId, ""),
      revision: this.numberValue(match.version, 0),
      serverTime: Date.now(),
      mode: match.mode === "head-to-head" ? "head-to-head" : "coop",
      payload: match,
    };
  }
  private record(value: unknown): Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  }
  private stringValue(value: unknown, fallback: string): string {
    return typeof value === "string" || typeof value === "number" ? String(value) : fallback;
  }
  private numberValue(value: unknown, fallback: number): number {
    return typeof value === "number" && Number.isFinite(value) ? value : fallback;
  }
  private scheduleReconnect(): void {
    if (this.intentionalClose || this.reconnectTimer !== null || this.reconnectAttempts >= 6)
      return;
    this.reconnectAttempts += 1;
    const delay = Math.min(15_000, 750 * 2 ** (this.reconnectAttempts - 1));
    this.setState("reconnecting");
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect().catch(() => this.scheduleReconnect());
    }, delay);
  }

  private fail(message: string): Error {
    this.lastError = message;
    this.setState("error");
    defenseAnalytics.track("multiplayer_error", { message });
    return new Error(message);
  }
  private setState(state: ConnectionState): void {
    this.state = state;
    this.emit();
  }
  private emit(): void {
    for (const listener of this.listeners) listener();
  }
}

export const defenseMultiplayer = new DefenseMultiplayerClient();
